import React, { useState, useEffect } from 'react';
import { useAuth, authFetch } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_BADGE = {
  super_admin: { label: 'Super Admin', cls: 'border-tf-accent text-tf-accent' },
  admin:       { label: 'Admin',       cls: 'border-tf-primary text-tf-primary' },
  viewer:      { label: 'Viewer',      cls: 'border-tf-text-2 text-tf-text-2' },
};

export default function AdminManagement() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'viewer' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin';

  const fetchAdmins = async () => {
    try { const res = await authFetch(`${API}/api/admins`); if (res.ok) { setAdmins((await res.json()).admins || []); } } catch (e) {}
  };
  useEffect(() => { fetchAdmins(); }, []);

  const createAdmin = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const res = await authFetch(`${API}/api/admins`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (!res.ok) { setError((await res.json()).detail || 'Failed'); } else { setShowForm(false); setForm({ email: '', password: '', name: '', role: 'viewer' }); fetchAdmins(); }
    } catch (e) { setError('Network error'); } finally { setLoading(false); }
  };

  const updateRole = async (email, newRole) => {
    try { await authFetch(`${API}/api/admins/role`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, role: newRole }) }); fetchAdmins(); } catch (e) {}
  };

  const deleteAdmin = async (email) => {
    if (!window.confirm(`Delete admin ${email}?`)) return;
    try { await authFetch(`${API}/api/admins/${encodeURIComponent(email)}`, { method: 'DELETE' }); fetchAdmins(); } catch (e) {}
  };

  return (
    <div className="bg-tf-surface border border-tf-primary" data-testid="admin-management">
      <div className="px-5 py-4 border-b border-tf-primary flex items-center justify-between">
        <h2 className="font-heading font-bold text-sm tracking-[0.2em] uppercase text-tf-text">Admin Users</h2>
        {isSuperAdmin && (
          <button data-testid="add-admin-btn" onClick={() => setShowForm(!showForm)}
            className="px-3 py-1.5 bg-tf-accent text-white font-mono text-[9px] tracking-[0.15em] uppercase hover:bg-tf-accent-hover transition-all duration-300">
            {showForm ? 'Cancel' : 'Add Admin'}
          </button>
        )}
      </div>
      {showForm && isSuperAdmin && (
        <form onSubmit={createAdmin} className="px-5 py-4 border-b border-tf-border-muted bg-tf-bg space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="admin-name-input" type="text" placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="px-3 py-2 border border-tf-border-light bg-tf-surface font-mono text-xs focus:outline-none focus:border-tf-primary" />
            <input data-testid="admin-email-input" type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className="px-3 py-2 border border-tf-border-light bg-tf-surface font-mono text-xs focus:outline-none focus:border-tf-primary" />
            <input data-testid="admin-password-input" type="password" placeholder="Password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required className="px-3 py-2 border border-tf-border-light bg-tf-surface font-mono text-xs focus:outline-none focus:border-tf-primary" />
            <select data-testid="admin-role-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="px-3 py-2 border border-tf-border-light bg-tf-surface font-mono text-xs focus:outline-none focus:border-tf-primary">
              <option value="viewer">Viewer</option><option value="admin">Admin</option><option value="super_admin">Super Admin</option>
            </select>
          </div>
          {error && <p className="font-mono text-[10px] text-tf-danger">{error}</p>}
          <button data-testid="create-admin-submit" type="submit" disabled={loading} className="px-4 py-2 bg-tf-accent text-white font-mono text-[9px] tracking-[0.15em] uppercase hover:bg-tf-accent-hover disabled:opacity-50">{loading ? 'Creating...' : 'Create Admin'}</button>
        </form>
      )}
      <table className="w-full">
        <thead><tr className="border-b border-tf-border-muted">
          <th className="px-5 py-3 text-left font-mono text-[9px] tracking-[0.2em] uppercase text-tf-text-2">Name</th>
          <th className="px-5 py-3 text-left font-mono text-[9px] tracking-[0.2em] uppercase text-tf-text-2">Email</th>
          <th className="px-5 py-3 text-left font-mono text-[9px] tracking-[0.2em] uppercase text-tf-text-2">Role</th>
          {isSuperAdmin && <th className="px-5 py-3 text-left font-mono text-[9px] tracking-[0.2em] uppercase text-tf-text-2">Actions</th>}
        </tr></thead>
        <tbody>
          {admins.map((admin, i) => {
            const badge = ROLE_BADGE[admin.role] || ROLE_BADGE.viewer;
            return (
              <tr key={admin.email} className="border-b border-tf-border-muted last:border-b-0 hover:bg-tf-bg transition-colors" data-testid={`admin-row-${i}`}>
                <td className="px-5 py-3 font-body text-sm font-medium text-tf-text">{admin.name}</td>
                <td className="px-5 py-3 font-mono text-xs text-tf-text-2">{admin.email}</td>
                <td className="px-5 py-3"><span className={`inline-block px-2 py-0.5 border font-mono text-[9px] tracking-[0.15em] uppercase ${badge.cls}`}>{badge.label}</span></td>
                {isSuperAdmin && (
                  <td className="px-5 py-3">
                    {admin.email !== user?.email && (
                      <div className="flex gap-1">
                        <select data-testid={`role-select-${i}`} value={admin.role} onChange={e => updateRole(admin.email, e.target.value)} className="px-2 py-0.5 border border-tf-border-light font-mono text-[9px] bg-tf-surface focus:outline-none">
                          <option value="viewer">Viewer</option><option value="admin">Admin</option><option value="super_admin">Super Admin</option>
                        </select>
                        <button data-testid={`delete-admin-${i}`} onClick={() => deleteAdmin(admin.email)} className="px-2 py-0.5 border border-tf-danger text-tf-danger font-mono text-[8px] tracking-[0.1em] uppercase hover:bg-tf-danger hover:text-white transition-all">Delete</button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
